import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background-color: #fff;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #007bff;
    color: #007bff;
    background-color: #f8f9fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;
const HiddenInput = styled.input`
  display: none;
`;
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f8f9fa;
`;
const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #007bff;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;
const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FileSize = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;
const RemoveButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  color: #dc3545;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
  }
`;
const ErrorMessage = styled.div`
  font-size: 13px;
  color: #dc3545;
  padding: 8px 12px;
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 6px;
`;
const InfoText = styled.div`
  font-size: 12px;
  color: #666;
  padding: 0 4px;
`;
/**
 * FileUpload Component
 * File selection UI with preview and validation
 */ export const FileUpload = ({ onFileSelected, allowedTypes = [
    'jpg',
    'png',
    'gif',
    'pdf',
    'docx',
    'xlsx'
], maxSizeBytes = 26214400, disabled = false })=>{
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const formatFileSize = (bytes)=>{
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    const getFileExtension = (filename)=>{
        return filename.split('.').pop()?.toLowerCase() || '';
    };
    const validateFile = (file)=>{
        const extension = getFileExtension(file.name);
        if (!allowedTypes.includes(extension)) {
            return `File type not allowed. Allowed: ${allowedTypes.join(', ')}`;
        }
        if (file.size > maxSizeBytes) {
            return `File too large. Maximum size: ${formatFileSize(maxSizeBytes)}`;
        }
        return null;
    };
    const handleFileSelect = (e)=>{
        const file = e.target.files?.[0];
        setError(null);
        if (!file) {
            setSelectedFile(null);
            return;
        }
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }
        setSelectedFile(file);
        onFileSelected(file);
    };
    const handleRemove = ()=>{
        setSelectedFile(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const handleButtonClick = ()=>{
        fileInputRef.current?.click();
    };
    return /*#__PURE__*/ _jsxs(Container, {
        "data-testid": "file-upload-container",
        children: [
            /*#__PURE__*/ _jsxs(UploadButton, {
                onClick: handleButtonClick,
                disabled: disabled,
                "data-testid": "file-upload-button",
                children: [
                    /*#__PURE__*/ _jsx("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor",
                        children: /*#__PURE__*/ _jsx("path", {
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: 2,
                            d: "M12 4v16m8-8H4"
                        })
                    }),
                    "Choose File"
                ]
            }),
            /*#__PURE__*/ _jsx(HiddenInput, {
                ref: fileInputRef,
                type: "file",
                accept: allowedTypes.map((t)=>`.${t}`).join(','),
                onChange: handleFileSelect,
                disabled: disabled,
                "data-testid": "file-upload-input"
            }),
            error && /*#__PURE__*/ _jsx(ErrorMessage, {
                "data-testid": "file-upload-error",
                children: error
            }),
            selectedFile && /*#__PURE__*/ _jsx(PreviewContainer, {
                children: /*#__PURE__*/ _jsxs(FilePreview, {
                    "data-testid": "file-upload-preview",
                    children: [
                        /*#__PURE__*/ _jsx(FileIcon, {
                            "data-testid": "file-upload-icon",
                            children: getFileExtension(selectedFile.name).toUpperCase()
                        }),
                        /*#__PURE__*/ _jsxs(FileInfo, {
                            children: [
                                /*#__PURE__*/ _jsx(FileName, {
                                    "data-testid": "file-upload-name",
                                    children: selectedFile.name
                                }),
                                /*#__PURE__*/ _jsx(FileSize, {
                                    "data-testid": "file-upload-size",
                                    children: formatFileSize(selectedFile.size)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(RemoveButton, {
                            onClick: handleRemove,
                            "data-testid": "file-upload-remove",
                            type: "button",
                            children: "✕"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(InfoText, {
                children: [
                    "Max ",
                    formatFileSize(maxSizeBytes),
                    " • ",
                    allowedTypes.join(', ')
                ]
            })
        ]
    });
};
